import { useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DragOverlay,
  UniqueIdentifier,
  useDroppable,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Menu, MenuSection, MenuItem } from '../../../types/menu'
import { needsReview } from '../../../utils/menuReview'

interface MenuEditingInterfaceProps {
  menu: Menu
  onChange: (updatedMenu: Menu) => void
}

interface DeleteModalState {
  type: 'section' | 'item' | null
  sectionId?: string
  itemId?: string
  title?: string
}

// Sortable Section Component
const SortableSection = ({
  section,
  isExpanded,
  onToggle,
  onDeleteSection,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onEditSection,
  onUpdateSection,
  editingItem,
  editingSection,
  editingItemData,
  onSaveItem,
  onCancelItem,
  onUpdateEditingItem,
  currency,
}: {
  section: MenuSection
  isExpanded: boolean
  onToggle: (id: string) => void
  onDeleteSection: (id: string, name: string) => void
  onAddItem: (sectionId: string) => void
  onEditItem: (itemId: string) => void
  onDeleteItem: (sectionId: string, itemId: string, name: string) => void
  onEditSection: (sectionId: string) => void
  onUpdateSection: (sectionId: string, updates: Partial<MenuSection>) => void
  editingItem: string | null
  editingSection: string | null
  editingItemData: MenuItem | null
  onSaveItem: () => void
  onCancelItem: () => void
  onUpdateEditingItem: (updates: Partial<MenuItem>) => void
  currency: string
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id })

  // Make the section droppable for items
  const { setNodeRef: setDroppableRef } = useDroppable({
    id: section.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={{...style, borderRadius: '10px', backgroundColor: '#FFF', boxShadow: '0px 9px 10px 0px rgba(0, 0, 0, 0.03)', borderStyle: 'none'}}
      className={`border-b border-gray-200 ${isDragging ? 'bg-blue-50 shadow-lg' : ''} px-[30px] py-[25px]`}
    >
      {/* Section Header */}
      <div
        className="flex items-center justify-between cursor-pointer hover:bg-gray-50"
        onClick={() => onToggle(section.id)}
      >
        <div className="flex items-center space-x-3">
          <div
            {...attributes}
            {...listeners}
            className="flex items-center justify-center px-3 py-2 cursor-grab active:cursor-grabbing hover:bg-gray-100 rounded-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col space-y-1">
              <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
              <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
              <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {editingSection === section.id ? (
              <input
                type="text"
                value={section.name}
                onChange={(e) =>
                  onUpdateSection(section.id, { name: e.target.value })
                }
                onBlur={() => onEditSection('')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    onEditSection('')
                  }
                }}
                className="menu-section-title text-custom-dark bg-transparent border-none outline-none focus:ring-2 focus:ring-brand-primary rounded px-1 py-0.5"
                autoFocus
              />
            ) : (
              <h3
                className="menu-section-title text-custom-dark cursor-pointer hover:bg-gray-100 rounded px-1 py-0.5"
                onClick={(e) => {
                  e.stopPropagation()
                  onEditSection(section.id)
                }}
              >
                {section.name || <span className="text-gray-400 italic">Click to add title</span>}
              </h3>
            )}
            <svg
              className={`w-4 h-4 text-gray-500 transition-transform ${
                isExpanded ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <select
            value={section.column || 'column1'}
            onChange={(e) => {
              e.stopPropagation()
              onUpdateSection(section.id, { column: e.target.value as 'column1' | 'column2' })
            }}
            onClick={(e) => e.stopPropagation()}
            className="px-3 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary bg-white text-gray-700 min-w-[80px]"
          >
            <option value="column1">Col 1</option>
            <option value="column2">Col 2</option>
          </select>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onDeleteSection(section.id, section.name)
            }}
            className="w-8 h-8 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center justify-center"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onAddItem(section.id)
            }}
            className="w-8 h-8 text-white rounded-md hover:opacity-80 flex items-center justify-center bg-brand-primary"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Section Items */}
      {isExpanded && (
        <div className="">
          <SortableContext
            items={section.items.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <div
              ref={setDroppableRef}
              className={section.items.length === 0 ? "min-h-[100px]" : "min-h-[20px]"}
              style={{gap: '10px', display: 'flex', flexDirection: 'column'}}
            >
              {section.items.length === 0 && (
                <div className="flex items-center justify-center h-[100px] border-2 border-dashed border-gray-300 rounded-md bg-gray-50 pointer-events-none">
                  <p className="text-sm text-gray-500">Drag items here or click + to add</p>
                </div>
              )}
              {section.items.map((item) => (
                <SortableItem
                  key={item.id}
                  item={item}
                  sectionId={section.id}
                  isEditing={editingItem === item.id}
                  onEdit={onEditItem}
                  onDelete={onDeleteItem}
                  editingItemData={editingItemData}
                  onSaveItem={onSaveItem}
                  onCancelItem={onCancelItem}
                  onUpdateEditingItem={onUpdateEditingItem}
                  currency={currency}
                />
              ))}
            </div>
          </SortableContext>
        </div>
      )}
    </div>
  )
}

// Sortable Item Component
const SortableItem = ({
  item,
  sectionId,
  isEditing,
  onEdit,
  onDelete,
  editingItemData,
  onSaveItem,
  onCancelItem,
  onUpdateEditingItem,
  currency,
}: {
  item: MenuItem
  sectionId: string
  isEditing: boolean
  onEdit: (itemId: string) => void
  onDelete: (sectionId: string, itemId: string, name: string) => void
  editingItemData: MenuItem | null
  onSaveItem: () => void
  onCancelItem: () => void
  onUpdateEditingItem: (updates: Partial<MenuItem>) => void
  currency: string
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        borderRadius: '10px',
        border: needsReview(item) ? '1px solid #F59E0B' : '0.5px solid rgba(14, 59, 67, 0.20)',
        backgroundColor: needsReview(item) ? '#FFFBEB' : undefined,
        padding: '18px 30px',
        paddingLeft: '0px',
      }}
      className={`individual-item-of-menu-editor flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0 ${isDragging ? 'bg-white shadow-lg' : ''}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="flex items-center justify-center px-3 py-2 cursor-grab active:cursor-grabbing hover:bg-gray-100 rounded-md mr-3 min-w-[30px]"
      >
        <div className="flex flex-col space-y-1">
          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
        </div>
      </div>
      <div className="flex-1">
        {isEditing ? (
          <div className="space-y-2">
            <input
              type="text"
              value={editingItemData?.name || ''}
              onChange={(e) =>
                onUpdateEditingItem({ name: e.target.value })
              }
              className="w-full menu-item-name-price border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-primary"
              autoFocus
            />
            <div className="relative w-24">
              {currency && <span className="absolute left-2 top-1 text-custom-dark">{currency}</span>}
              <input
                type="text"
                value={String(editingItemData?.price || '').replace(/[^\d.]/g, '')}
                onChange={(e) => {
                  // Only allow numbers and decimal point
                  const value = e.target.value.replace(/[^\d.]/g, '')
                  // Ensure only one decimal point
                  const parts = value.split('.')
                  const sanitizedValue = parts.length > 2
                    ? parts[0] + '.' + parts.slice(1).join('')
                    : value
                  onUpdateEditingItem({ price: sanitizedValue })
                }}
                className={`w-full menu-item-name-price border border-gray-300 rounded ${currency ? 'pl-5' : 'pl-2'} pr-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-primary`}
                placeholder="0.00"
              />
            </div>
            <textarea
              value={editingItemData?.description || ''}
              onChange={(e) =>
                onUpdateEditingItem({ description: e.target.value })
              }
              className="w-full menu-item-description border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-primary"
              style={{textAlign: 'left'}}
              placeholder="Description"
              rows={2}
            />
            <div className="flex space-x-2">
              <button
                onClick={onSaveItem}
                className="text-xs text-white px-3 py-1 rounded hover:opacity-80 bg-brand-primary"
              >
                Save
              </button>
              <button
                onClick={onCancelItem}
                className="text-xs bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="cursor-pointer" onClick={() => onEdit(item.id)}>
            <div
              className="flex items-center"
              style={{ gap: '20px' }}
            >
              <span className="menu-item-name-price text-custom-dark" style={{fontWeight: 'bold'}}>
                {item.name}
              </span>
              {item.price !== undefined && String(item.price).replace(/[^\d.]/g, '') !== '' && (
                <>
                  <span className="menu-item-name-price text-custom-dark" style={{fontWeight: 'bold'}}>|</span>
                  <span className="menu-item-name-price text-custom-dark" style={{fontWeight: 'bold'}}>
                    {currency}{String(item.price).replace(/[^\d.]/g, '')}
                  </span>
                </>
              )}
              {needsReview(item) && (
                <span
                  className="text-xs font-medium text-amber-800 bg-amber-100 rounded-full px-2 py-0.5"
                  title="Claude wasn't sure it read this item correctly. Click to check and save."
                >
                  Please review
                </span>
              )}
            </div>
            {item.description && (
              <p
                className="menu-item-description text-gray-600"
                style={{ textAlign: 'left', marginTop: '10.5px', whiteSpace: 'pre-line' }}
              >
                {item.description}
              </p>
            )}
          </div>
        )}
      </div>
      <button
        onClick={() => onDelete(sectionId, item.id, item.name)}
        className="ml-4 text-gray-400 hover:text-red-600"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  )
}

export const MenuEditingInterface = ({
  menu,
  onChange,
}: MenuEditingInterfaceProps) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(menu.sections.map((s) => s.id))
  )
  const [editingItem, setEditingItem] = useState<string | null>(null)
  const [editingSection, setEditingSection] = useState<string | null>(null)
  const [editingItemData, setEditingItemData] = useState<MenuItem | null>(null)
  const [deleteModal, setDeleteModal] = useState<DeleteModalState>({
    type: null,
  })
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)
  const [savedExpandedSections, setSavedExpandedSections] = useState<Set<string> | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId)
    } else {
      newExpanded.add(sectionId)
    }
    setExpandedSections(newExpanded)
  }

  const updateMenuItem = (
    sectionId: string,
    itemId: string,
    updates: Partial<MenuItem>
  ) => {
    const updatedMenu = {
      ...menu,
      sections: menu.sections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              items: section.items.map((item) =>
                item.id === itemId ? { ...item, ...updates } : item
              ),
            }
          : section
      ),
      lastModified: new Date(),
    }
    onChange(updatedMenu)
  }

  const updateSection = (sectionId: string, updates: Partial<MenuSection>) => {
    const updatedMenu = {
      ...menu,
      sections: menu.sections.map((section) =>
        section.id === sectionId ? { ...section, ...updates } : section
      ),
      // Auto-switch to 2 columns if assigning a section to column2 while in 1-column layout
      layout: (updates.column === 'column2' && menu.layout === '1col') ? '2col' : menu.layout,
      lastModified: new Date(),
    }
    onChange(updatedMenu)
  }

  const deleteMenuItem = () => {
    if (!deleteModal.sectionId || !deleteModal.itemId) return

    const updatedMenu = {
      ...menu,
      sections: menu.sections.map((section) =>
        section.id === deleteModal.sectionId
          ? {
              ...section,
              items: section.items.filter(
                (item) => item.id !== deleteModal.itemId
              ),
            }
          : section
      ),
      lastModified: new Date(),
    }
    onChange(updatedMenu)
    setDeleteModal({ type: null })
  }

  const deleteSection = () => {
    if (!deleteModal.sectionId) return

    const updatedMenu = {
      ...menu,
      sections: menu.sections.filter(
        (section) => section.id !== deleteModal.sectionId
      ),
      lastModified: new Date(),
    }
    onChange(updatedMenu)
    setDeleteModal({ type: null })
  }

  const addSection = () => {
    const newSection: MenuSection = {
      id: generateId(),
      name: 'New Section',
      items: [],
      order: menu.sections.length + 1,
      column: 'column1',
    }

    const updatedMenu = {
      ...menu,
      sections: [...menu.sections, newSection],
      lastModified: new Date(),
    }

    onChange(updatedMenu)
    setExpandedSections((prev) => new Set([...prev, newSection.id]))
  }

  const addMenuItem = (sectionId: string) => {
    const newItem: MenuItem = {
      id: generateId(),
      name: 'Item',
      price: '0.00',
      order:
        (menu.sections.find((s) => s.id === sectionId)?.items.length || 0) + 1,
    }

    const updatedMenu = {
      ...menu,
      sections: menu.sections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              items: [...section.items, newItem],
            }
          : section
      ),
      lastModified: new Date(),
    }

    onChange(updatedMenu)
    setEditingItem(newItem.id)
    setEditingItemData({ ...newItem })
  }

  const generateId = () => Math.random().toString(36).substr(2, 9)

  const startEditingItem = (itemId: string) => {
    // Save current item data before editing
    const item = menu.sections.flatMap(s => s.items).find(i => i.id === itemId)
    if (item) {
      // Clean the price when loading for editing
      const cleanedItem = {
        ...item,
        price: item.price ? String(item.price).replace(/[^\d.]/g, '') : item.price
      }
      setEditingItemData(cleanedItem)
      setEditingItem(itemId)
    }
  }

  const saveItemChanges = () => {
    if (editingItemData && editingItem) {
      // Find the section containing the item
      const sectionId = menu.sections.find(s =>
        s.items.some(i => i.id === editingItem)
      )?.id

      if (sectionId) {
        // Clean the price to remove any currency symbols before saving
        const cleanedData = {
          ...editingItemData,
          price: editingItemData.price ? String(editingItemData.price).replace(/[^\d.]/g, '') : editingItemData.price,
          // Saving counts as reviewed, so the "Please review" flag goes away
          confidence: 1,
        }
        // Save the edited data to the menu
        updateMenuItem(sectionId, editingItem, cleanedData)
      }
    }
    setEditingItem(null)
    setEditingItemData(null)
  }

  const cancelItemChanges = () => {
    // Discard changes and close editing
    setEditingItem(null)
    setEditingItemData(null)
  }

  const updateEditingItemData = (updates: Partial<MenuItem>) => {
    if (editingItemData) {
      setEditingItemData({ ...editingItemData, ...updates })
    }
  }

  const handleDragStart = (event: DragStartEvent) => {
    const id = event.active.id as string
    setActiveId(id)

    // Check if we're dragging a section
    const section = menu.sections.find((s) => s.id === id)
    if (section) {
      // Save current expanded state and collapse all sections when dragging a section
      setSavedExpandedSections(new Set(expandedSections))
      setExpandedSections(new Set())
    }
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event

    if (!over) return

    const activeId = active.id
    const overId = over.id

    // Check if we're moving an item between sections
    const activeItem = findItemById(activeId as string)
    const overItem = findItemById(overId as string)

    if (activeItem && overItem && activeItem.sectionId !== overItem.sectionId) {
      const updatedMenu = moveItemBetweenSections(
        activeItem.sectionId,
        overItem.sectionId,
        activeItem.item,
        overItem.index
      )
      onChange(updatedMenu)
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)

    // Restore expanded sections if we were dragging a section
    if (savedExpandedSections) {
      setExpandedSections(savedExpandedSections)
      setSavedExpandedSections(null)
    }

    if (!over) return

    const activeId = active.id
    const overId = over.id

    if (activeId === overId) return

    // Check if we're reordering sections
    const activeSectionIndex = menu.sections.findIndex((s) => s.id === activeId)
    const overSectionIndex = menu.sections.findIndex((s) => s.id === overId)

    if (activeSectionIndex !== -1 && overSectionIndex !== -1) {
      // Reorder sections
      const newSections = arrayMove(
        menu.sections,
        activeSectionIndex,
        overSectionIndex
      )
      const updatedSections = newSections.map((section, index) => ({
        ...section,
        order: index + 1,
      }))

      const updatedMenu = {
        ...menu,
        sections: updatedSections,
        lastModified: new Date(),
      }
      onChange(updatedMenu)
      return
    }

    // Check if we're reordering items within the same section
    const activeItem = findItemById(activeId as string)
    const overItem = findItemById(overId as string)

    if (activeItem && overItem && activeItem.sectionId === overItem.sectionId) {
      const section = menu.sections.find((s) => s.id === activeItem.sectionId)
      if (!section) return

      const oldIndex = section.items.findIndex((item) => item.id === activeId)
      const newIndex = section.items.findIndex((item) => item.id === overId)

      const newItems = arrayMove(section.items, oldIndex, newIndex)
      const updatedItems = newItems.map((item, index) => ({
        ...item,
        order: index + 1,
      }))

      const updatedMenu = {
        ...menu,
        sections: menu.sections.map((s) =>
          s.id === activeItem.sectionId ? { ...s, items: updatedItems } : s
        ),
        lastModified: new Date(),
      }
      onChange(updatedMenu)
      return
    }

    // Handle moving items between sections (including into empty sections)
    if (activeItem && !overItem) {
      // We're dragging an item, but didn't drop over another item
      // Check if we dropped over a section (for empty sections)
      const targetSection = menu.sections.find((s) => {
        // Check if any items in this section match the overId, or if the section itself matches
        return s.items.some(item => item.id === overId)
      })

      if (!targetSection) {
        // Maybe we dropped directly into an empty section's container
        // In dnd-kit, the section's SortableContext container might be the drop target
        // We need to check if overId could be a section ID
        const possibleSection = menu.sections.find(s => s.id === overId)
        if (possibleSection && activeItem.sectionId !== possibleSection.id) {
          // Move item to the empty section
          const updatedMenu = moveItemBetweenSections(
            activeItem.sectionId,
            possibleSection.id,
            activeItem.item,
            0 // Add to beginning of empty section
          )
          onChange(updatedMenu)
        }
      }
    }
  }

  const findItemById = (id: string) => {
    for (const section of menu.sections) {
      const itemIndex = section.items.findIndex((item) => item.id === id)
      if (itemIndex !== -1) {
        return {
          item: section.items[itemIndex],
          sectionId: section.id,
          index: itemIndex,
        }
      }
    }
    return null
  }

  const moveItemBetweenSections = (
    sourceSectionId: string,
    destSectionId: string,
    item: MenuItem,
    destIndex: number
  ) => {
    const newSections = menu.sections.map((section) => {
      if (section.id === sourceSectionId) {
        // Remove item from source section
        return {
          ...section,
          items: section.items
            .filter((i) => i.id !== item.id)
            .map((item, index) => ({
              ...item,
              order: index + 1,
            })),
        }
      } else if (section.id === destSectionId) {
        // Add item to destination section
        const newItems = [...section.items]
        newItems.splice(destIndex, 0, item)
        return {
          ...section,
          items: newItems.map((item, index) => ({
            ...item,
            order: index + 1,
          })),
        }
      }
      return section
    })

    return {
      ...menu,
      sections: newSections,
      lastModified: new Date(),
    }
  }

  const allSectionIds = menu.sections.map((section) => section.id)

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-0" style={{gap: '25px', display: 'flex', flexDirection: 'column'}}>
        {/* Sections */}
        <SortableContext
          items={allSectionIds}
          strategy={verticalListSortingStrategy}
        >
          {menu.sections.map((section) => (
            <SortableSection
              key={section.id}
              section={section}
              isExpanded={expandedSections.has(section.id)}
              onToggle={toggleSection}
              onDeleteSection={(id, name) =>
                setDeleteModal({ type: 'section', sectionId: id, title: name })
              }
              onAddItem={addMenuItem}
              onEditItem={startEditingItem}
              onDeleteItem={(sectionId, itemId, name) =>
                setDeleteModal({ type: 'item', sectionId, itemId, title: name })
              }
              onEditSection={setEditingSection}
              onUpdateSection={updateSection}
              editingItem={editingItem}
              editingSection={editingSection}
              editingItemData={editingItemData}
              onSaveItem={saveItemChanges}
              onCancelItem={cancelItemChanges}
              onUpdateEditingItem={updateEditingItemData}
              currency={menu.currency || ''}
            />
          ))}
        </SortableContext>

        {/* Add Section Button */}
        <div className="p-4">
          <button
            onClick={addSection}
            className="flex items-center space-x-2 hover:opacity-80 font-medium text-brand-primary"
          >
            <div className="w-8 h-8 text-white rounded-md hover:opacity-80 flex items-center justify-center bg-brand-primary">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            </div>
            <h3 className='menu-section-title'>Add section</h3>
          </button>
        </div>

        {/* Delete Confirmation Modal */}
        {deleteModal.type && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-medium text-custom-dark mb-4">
                Are you sure you want to delete this {deleteModal.type}?
              </h3>
              {deleteModal.title && (
                <p className="text-sm text-gray-600 mb-6">
                  "{deleteModal.title}" will be permanently removed.
                </p>
              )}
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setDeleteModal({ type: null })}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={
                    deleteModal.type === 'section'
                      ? deleteSection
                      : deleteMenuItem
                  }
                  className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-80 bg-brand-primary"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <DragOverlay dropAnimation={null}>
        {activeId ? null : null}
      </DragOverlay>
    </DndContext>
  )
}

export default MenuEditingInterface
